import React, { useEffect, useState, useRef, useId } from 'react';
import PropTypes from 'prop-types';
import "../../Styles/Dropdown.1.css";

const AccessibleDropDown = (props) => {
    const labelId = useId();
    const optionId = (key) => `${labelId}-option-${key}`;
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const dropdownMenuRef = useRef(null);
    const [initialOpen, setInitialOpen] = useState(false);
    const resetTimer = useRef(null);
    const [typedChars, setTypedChars] = useState('');
    const [lastChar, setLastChar] = useState('');

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
          setHighlightedIndex(selectedIndex);
          setInitialOpen(false);
        }
      };

    useEffect(() => {
        var index = props.Items.findIndex(a => a[0] === props.SelectedItem);
        setHighlightedIndex(index);
        setSelectedIndex(index);

        return () => {
            if (resetTimer.current) {
              clearTimeout(resetTimer.current);
            }
          };
    }, [props.Items, props.SelectedItem]);
    
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const scrollOpenedMenuIntoView = () => {
        setTimeout(() => {
            const selectedElement = props.SelectedItem
                ? document.getElementById(optionId(props.SelectedItem))
                : null;
            if (selectedElement) {
                selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
                // scroll the menu into view if no item is selected
                dropdownMenuRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 0);
    }

    const toggleDropdown = () => {
        if(isOpen){
            setHighlightedIndex(selectedIndex);
            setInitialOpen(false);
        }
        else{
            setInitialOpen(true);
            scrollOpenedMenuIntoView();
        }
        setIsOpen(!isOpen);
    }

    const openDropdown = () => {
        setInitialOpen(true);
        scrollOpenedMenuIntoView();
        setIsOpen(true);
    }

    const handleKeyDown = (event) => {
        setInitialOpen(false);
        const { key, ctrlKey, keyCode } = event; 
        const maxIndex = props.Items.length - 1;
        let newIndex = highlightedIndex;

        if (keyCode >= 112 && keyCode <= 123) {
            return; //ignore function keys
        }
        
        if (!isOpen) {
            if(key === "Space" || key === " "){
                event.preventDefault();
                openDropdown();
                return;
            }
            switch (key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    const nextIndex = changeSelection(1);
                    handleSelectChange(props.Items[nextIndex][0]);
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    const prevIndex = changeSelection(-1);
                    handleSelectChange(props.Items[prevIndex][0]);
                    break;
                }
                case 'End':
                    event.preventDefault();
                    changeSelectionTo(maxIndex);
                    break;
                case 'Home':
                    event.preventDefault();
                    changeSelectionTo(0);
                    break;
                default:
                    if (key.length === 1) {
                        event.preventDefault();
                        handleTypeAhead(key);
                    }
                    break;
            }
        }
        else{
            if (ctrlKey) {
                switch (key) {
                    case 'End':
                        event.preventDefault();
                        setHighlightedIndex(maxIndex);
                        scrollToItem(maxIndex);
                        clearTimerAndChars();
                        break;
                    case 'Home':
                        event.preventDefault(); 
                        setHighlightedIndex(0);
                        scrollToItem(0);
                        clearTimerAndChars();
                        break;
                    case 'Tab':
                        setIsOpen(false);
                        setHighlightedIndex(selectedIndex);
                        clearTimerAndChars();
                        break;
                    default:
                        break;
                }
            }
            else{
                switch (key) {
                    case 'ArrowDown':
                        event.preventDefault();
                        clearTimerAndChars();
                        newIndex = (highlightedIndex < maxIndex) ? highlightedIndex + 1 : 0;
                        setHighlightedIndex(newIndex);
                        scrollToItem(newIndex);
                        break;
                    case 'Tab':
                        setIsOpen(false);
                        setHighlightedIndex(selectedIndex);
                        clearTimerAndChars();
                        break;
                    case 'ArrowUp':
                        event.preventDefault();
                        clearTimerAndChars();
                        setHighlightedIndex((prevIndex) => (prevIndex === null || prevIndex === 0) ? maxIndex : prevIndex - 1);
                        scrollToItem(newIndex);
                        break;
                    case 'PageDown': {
                        event.preventDefault();
                        clearTimerAndChars();
                        const jumpDownIndex = Math.min(highlightedIndex + 10, maxIndex);
                        setHighlightedIndex(jumpDownIndex);
                        scrollToItem(jumpDownIndex);
                        break;
                    }
                    case 'PageUp': {
                        event.preventDefault();
                        clearTimerAndChars();
                        const jumpUpIndex = Math.max(highlightedIndex - 10, 0);
                        setHighlightedIndex(jumpUpIndex);
                        scrollToItem(jumpUpIndex);
                        break;
                    }
                    case 'End':
                        event.preventDefault();
                        clearTimerAndChars();
                        setHighlightedIndex(maxIndex);
                        scrollToItem(maxIndex);
                        break;
                    case 'Home':
                        event.preventDefault(); 
                        clearTimeout(resetTimer.current);
                        setHighlightedIndex(0);
                        scrollToItem(0);
                        break;
                    case 'Enter':
                        if (highlightedIndex !== null) {
                            event.preventDefault();
                            handleSelectChange(props.Items[highlightedIndex][0]);
                            setIsOpen(false);
                            clearTimerAndChars();
                        }
                        break;
                    case 'Escape':
                        setIsOpen(false);
                        setHighlightedIndex(selectedIndex);
                        clearTimerAndChars();
                        break;
                    default:
                        event.preventDefault();
                        handleTypeAhead(key);
                        break;
                    }
            }
        }
    };

    const clearTimerAndChars = () => {
        setTypedChars('');
        setLastChar('');
        clearTimeout(resetTimer.current);
    }

    const changeSelection = (direction) => {
        let newIndex = highlightedIndex + direction;
        if (newIndex < 0) newIndex = props.Items.length - 1;
        if (newIndex >= props.Items.length) newIndex = 0;
        setHighlightedIndex(newIndex);
        scrollToItem(newIndex);
        return newIndex;
    };

    const changeSelectionTo = (index) => {
        setHighlightedIndex(index);
        scrollToItem(index);
        handleSelectChange(props.Items[index][0]);
    }

    const handleTypeAhead = (key) => {
        if (key.length === 1) {
            clearTimeout(resetTimer.current);

            const potentialSearchTerm = typedChars + key;

            const startsWithIndex = props.Items.findIndex(([, itemValue]) =>
                itemValue[props.NameField].toLowerCase().startsWith(potentialSearchTerm.toLowerCase()));

            if (startsWithIndex === -1 && key === lastChar)
                cycleThroughSameStartingChar(key)
            if (startsWithIndex !== -1 && key === lastChar) {
                setTypedChars(potentialSearchTerm);
                setHighlightedIndex(startsWithIndex);
                scrollToItem(startsWithIndex);
                if (!isOpen) handleSelectChange(props.Items[startsWithIndex][0]);
            } else {
                setTypedChars(potentialSearchTerm);
                findAndHighlightOption(potentialSearchTerm);
            }

            setLastChar(key);

            resetTimer.current = setTimeout(() => {
                setTypedChars('');
                setLastChar('');
            }, 1000);
          }
    }

    const cycleThroughSameStartingChar = (char) => {
        const startIndex = highlightedIndex + 1;
        const newIndex = props.Items.slice(startIndex).findIndex(([, itemValue]) =>
            itemValue[props.NameField].toLowerCase().startsWith(char.toLowerCase())) + startIndex;
    
        if (newIndex !== -1 && newIndex !== highlightedIndex) {
            setHighlightedIndex(newIndex);
            scrollToItem(newIndex);
            if (!isOpen) handleSelectChange(props.Items[newIndex][0]);
        } else {
            const wrapAroundIndex = props.Items.findIndex(([, itemValue]) =>
                itemValue[props.NameField].toLowerCase().startsWith(char.toLowerCase()));
            if (wrapAroundIndex !== -1 && wrapAroundIndex !== highlightedIndex) {
                setHighlightedIndex(wrapAroundIndex);
                scrollToItem(wrapAroundIndex);
                if (!isOpen) handleSelectChange(props.Items[wrapAroundIndex][0]);
            }
        }
    };

    const findAndHighlightOption = (input) => {
        const newIndex = props.Items.findIndex(([, itemValue]) =>
            itemValue[props.NameField].toLowerCase().startsWith(input.toLowerCase()));
        if (newIndex !== -1) {
            setHighlightedIndex(newIndex);
            scrollToItem(newIndex);
            if (!isOpen) handleSelectChange(props.Items[newIndex][0]);
        }
    };

    const scrollToItem = (index) => {
        const optionElement = document.getElementById(optionId(props.Items[index][0]));
        const dropdownMenu = dropdownMenuRef.current;
    
        if (optionElement && dropdownMenu) {
            const dropdownRect = dropdownMenu.getBoundingClientRect();
            const optionRect = optionElement.getBoundingClientRect();
    
            if (optionRect.bottom > dropdownRect.bottom || optionRect.top < dropdownRect.top) {
                optionElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    };

    const handleSelectChange = (key) => {
        props.SelectChange(key);
        setIsOpen(false);
    }

    const startsWithVowel = (str) => {
        return /^[aeiou]/i.test(str);
    }

    return (
        <>
            <div
                className={`dropdown ${props.Disabled ? "disabled" : ""}`}
                role="combobox"
                aria-haspopup="listbox"
                aria-activedescendant={highlightedIndex !== -1 ? optionId(props.Items[highlightedIndex][0]) : undefined}
                aria-expanded={isOpen}
                aria-disabled={props.Disabled}
                aria-labelledby={labelId}
                ref={dropdownRef}
                tabIndex={props.Disabled ? -1 : 0}
                onClick={props.Disabled ? undefined : toggleDropdown}
                onKeyDown={props.Disabled ? undefined : handleKeyDown}
            >
                <div className="selected" id={labelId}>
                    {props.Dictionary &&
                    props.SelectedItem !== undefined &&
                    props.SelectedItem !== null &&
                    Object.prototype.hasOwnProperty.call(props.Dictionary, props.SelectedItem)
                        ? props.Dictionary[props.SelectedItem][props.NameField]
                        : `--Select ${startsWithVowel(props.ItemName) ? "an" : "a"} ${props.ItemName}--`}
                    <span className="arrow"></span>
                </div>
                <div
                    className={`dropdownMenu ${isOpen ? "show" : ""}`}
                    role="listbox"
                    ref={dropdownMenuRef}
                >
                    {props.Items.map(([key, value], index) => (
                        <div
                            key={key}
                            id={optionId(key)}
                            className={`dropdownItem ${key === props.SelectedItem && initialOpen
                                    ? "selectedOption"
                                    : index === highlightedIndex
                                        ? "highlighted"
                                        : ""
                                }`}
                            role="option"
                            aria-selected={key === props.SelectedItem}
                            tabIndex={props.Disabled ? -1 : 0}
                            onClick={props.Disabled ? undefined : () => handleSelectChange(key)}
                        >
                            {value[props.NameField]}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

AccessibleDropDown.propTypes = {
    Items: PropTypes.arrayOf(PropTypes.array).isRequired,
    Dictionary: PropTypes.object,
    SelectedItem: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    SelectChange: PropTypes.func.isRequired,
    NameField: PropTypes.string.isRequired,
    ItemName: PropTypes.string.isRequired,
    Disabled: PropTypes.bool,
};

export default AccessibleDropDown;